import { useRef } from 'react';
import { Button, Input, Space, message } from 'antd';
import { UploadOutlined, DownloadOutlined } from '@ant-design/icons';
import ReactMarkdown from 'react-markdown';

interface Props {
  value?: string;
  onChange?: (v: string) => void;
  max: number;
  'data-testid'?: string;
}

/**
 * Reusable markdown editor: AntD TextArea (its showCount + maxLength give the
 * live counter + hard cap for free) beside a react-markdown live preview, with
 * .md upload (FileReader) / download (Blob). No transform — `value` is the
 * exact string sent to the API. Controlled; drops into a Form.Item unchanged.
 */
export function MarkdownPromptEditor({ value = '', onChange, max, ...rest }: Props) {
  const fileRef = useRef<HTMLInputElement>(null);

  function handleUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const text = String(reader.result ?? '');
      if (text.length > max) {
        // Refuse rather than silently slicing: the client-side trim meant the
        // server's own .max() never fired, so an over-long upload was persisted
        // as a quietly truncated prompt — which could then be activated globally.
        // The counter alone can't distinguish that from a legitimately at-cap one.
        message.error(
          `That file is ${text.length.toLocaleString()} characters; the limit is ${max.toLocaleString()}. Trim it and upload again.`,
        );
        return;
      }
      onChange?.(text);
    };
    reader.onerror = () => message.error('Could not read that file.');
    reader.readAsText(file);
    e.target.value = ''; // allow re-uploading the same file
  }

  function handleDownload() {
    const blob = new Blob([value], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'prompt.md';
    a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <div data-testid={rest['data-testid']}>
      <Space style={{ marginBottom: 8 }}>
        <Button size="small" icon={<UploadOutlined />} onClick={() => fileRef.current?.click()}>
          Upload .md
        </Button>
        <Button size="small" icon={<DownloadOutlined />} onClick={handleDownload} disabled={!value}>
          Download
        </Button>
        <input
          ref={fileRef}
          type="file"
          accept=".md,.markdown,text/markdown"
          data-testid="md-upload-input"
          style={{ display: 'none' }}
          onChange={handleUpload}
        />
      </Space>
      <div style={{ display: 'flex', gap: 12 }}>
        <Input.TextArea
          value={value}
          onChange={(e) => onChange?.(e.target.value)}
          maxLength={max}
          showCount
          autoSize={{ minRows: 12, maxRows: 30 }}
          style={{ flex: 1, fontFamily: 'monospace' }}
        />
        <div
          style={{ flex: 1, overflow: 'auto', border: '1px solid #f0f0f0', borderRadius: 6, padding: 12 }}
        >
          <ReactMarkdown>{value}</ReactMarkdown>
        </div>
      </div>
    </div>
  );
}
